import { useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { hasPermission, ActionsEnum } from '@/lib/permissions';

/**
 * Permission questions, asked one way.
 *
 * The app had two habits this replaces: calling `hasPermission(permissions, …)`
 * with the array threaded through by hand at each site, and — worse — checking
 * `role?.name === 'Owner'` inline in JSX. The second is not a permission check
 * at all: roles are user-editable, so an clinic that renames Owner or
 * builds a second admin role silently loses or gains UI that way.
 *
 * `can('staff', 'manage')` reads as the sentence it is, and the hierarchy
 * (DELETE ⟹ MANAGE ⟹ VIEW) is applied in one place.
 */
export function useCan() {
  const { permissions } = useAuth();

  const can = useCallback(
    (resource: string, action: ActionsEnum = ActionsEnum.VIEW) =>
      hasPermission(permissions, resource, action),
    [permissions],
  );

  return useMemo(
    () => ({
      can,
      /** Read access. */
      canView: (resource: string) => can(resource, ActionsEnum.VIEW),
      /** Create / edit access. */
      canManage: (resource: string) => can(resource, ActionsEnum.MANAGE),
      /** Destructive access. */
      canDelete: (resource: string) => can(resource, ActionsEnum.DELETE),
      /**
       * Sight of pay: salary and revenue share. A separate right from `staff`
       * on purpose — administering people and seeing what they earn are
       * different jobs, and the API enforces the same split, so a client that
       * asked for compensation without this simply receives fields that are
       * not there.
       */
      canViewCompensation: () => can('staff_compensation', ActionsEnum.VIEW),
      canManageCompensation: () =>
        can('staff_compensation', ActionsEnum.MANAGE),
    }),
    [can],
  );
}
